import * as Service from 'types/service';
import { ButtonBase } from '@material-ui/core';
import { ConnectDragSource } from 'react-dnd';
import { DisplayTime } from '@tock/time';
import { IconExclamationCircle, IconGrab, IconPin } from '@tock/icons';
import { Instant } from 'js-joda';
import { Typography } from 'bento';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

interface PartyStatusIndicatorProps {
  /**
   * Callback drag ref. Must be attached to draggable portion of DOM.
   */
  dragRef?: ConnectDragSource;
  /**
   * true if the screen rendering this component allows table
   * assignments to be modified.
   */
  isAssignmentEnabled: boolean;
  /**
   * Called when status indicator is clicked
   */
  onClick(): void;
  /**
   * The party being displayed
   */
  party: Service.Party;
  /**
   * When true unassigned parties display a warning icon and style
   */
  showUnassignedWarning?: boolean;
}

const useStyles = makeStyles(({ colors, borderRadius }) => ({
  root: {
    background: colors.bgLighter,
    color: colors.fgDefault,
    alignItems: 'center',
    alignSelf: 'stretch',

    borderBottomRightRadius: borderRadius[100],
    borderTopRightRadius: borderRadius[100],
    cursor: 'grab',
    display: 'flex',
    flex: `0 0 6rem`,
    flexDirection: 'column',
    justifyContent: 'center',

    '&:hover': {
      color: colors.fgPrimaryBase,
      background: colors.bgLighter,
    },
  },
  danger: {
    backgroundColor: colors.bgDangerLighter,
    color: colors.fgDangerBase,
  },
}));

/**
 * Specialized button for guest list cards that renders status
 * information based on the state of a party.
 */
const PartyStatusIndicator: React.FC<React.PropsWithChildren<PartyStatusIndicatorProps>> = ({
  dragRef,
  onClick,
  isAssignmentEnabled,
  party,
  showUnassignedWarning,
}) => {
  const classes = useStyles();

  switch (party.state) {
    case 'EXPECTED':
    case 'ARRIVED':
    case 'PARTIALLY_ARRIVED': {
      const { tables, visit, tableString } = party;
      const hasPreAssignment = tables.length > 0;
      const isPinned = visit && visit.pinnedToTable;

      /**
       * If there is no pre-assignment and the assignments are not editable we don't
       * render a status. (use case: slot customers in timeline view)
       */
      if (!isAssignmentEnabled && !hasPreAssignment) {
        return null;
      }

      return hasPreAssignment ? (
        <ButtonBase
          ref={dragRef}
          className={classes.root}
          disableRipple
          onClick={onClick}
          aria-label={`View details for party ${
            isPinned ? `pinned` : `assigned`
          } to ${tableString}`}
        >
          <Typography variant="bodyStrong" color="inherit" aria-hidden="true">
            {tableString}
          </Typography>
          {isPinned && <IconPin data-testid="pinIcon" fontSize="small" />}
        </ButtonBase>
      ) : (
        <ButtonBase
          ref={dragRef}
          className={clsx(classes.root, { [classes.danger]: showUnassignedWarning })}
          disableRipple
          onClick={onClick}
          aria-label="View details for unassigned party"
        >
          {showUnassignedWarning ? (
            <IconExclamationCircle data-testid="alertIcon" color="error" />
          ) : (
            <IconGrab data-testid="dragIcon" />
          )}
        </ButtonBase>
      );
    }

    case 'PARTIALLY_SEATED':
    case 'SEATED': {
      const { seatedTime } = party;
      const hasSeatedTime = seatedTime !== undefined;

      return (
        <ButtonBase ref={dragRef} className={classes.root} disableRipple onClick={onClick}>
          <Typography variant="bodyStrong" color="inherit" mb={hasSeatedTime ? 0.5 : 0}>
            {party.tableString}
          </Typography>
          {hasSeatedTime ? (
            <Typography variant="captionStrong" color="subtle">
              <DisplayTime timeValue={Instant.ofEpochSecond(seatedTime)} />
            </Typography>
          ) : null}
        </ButtonBase>
      );
    }

    case 'NO_SHOW':
    case 'CANCELED':
    case 'LEFT':
      return null;

    default:
      assertNever(party.state);
  }
};

export default PartyStatusIndicator;
